import React, { useCallback, useContext, useMemo, useState } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";

import ViewportContext from "~/common/ViewportContext";
import { ScrollContext } from "~/layout/ScrollContext";

import { ReactComponent as PanelButtonIcon } from "./PanelButton.svg";

import styles from "./CollapsiblePanel.module.css";

const defaultContextValue = {
	collapsed: false,
	toggle: noop,
	set: noop,
};

interface CollapsiblePanelProps<PanelProps> {
	panel: React.ComponentType<PanelProps>;
	panelProps: PanelProps & JSX.IntrinsicAttributes;
	collapsed: boolean;
	onChange: (isCollapsed: boolean) => void;
	width: string;
	/** If false, the panel will be non-collapsible
	 * @default true if on mobile */
	enabled?: boolean;
}

declare module "csstype" {
	interface Properties {
		/** Our CSS variable to set the width of a CollapsiblePanel, augmenting the builtin React CSS types */
		"--collapsiblePanelWidth"?: string;
	}
}

export const PanelCollapseContext = React.createContext(defaultContextValue);
PanelCollapseContext.displayName = "PanelCollapseContext";
export const PanelCollapseConsumer = PanelCollapseContext.Consumer;

/**
 * Sets up a layout with a component `panel` in the panel on the left, and main content
 * on the right. It is **controlled**, i.e. it has no internal state. Whether or not it
 * is collapsed is managed via props `collapsed` and `onChange`.
 *
 * **Note:** On mobile, the default toggle button is hidden, and only the panel or the
 * main content can be visible at one time, so the main content should include a button
 * for opening the panel. (See [[MobileOpenPanelButton]])
 */
export default function CollapsiblePanel<PanelProps>({
	children,
	collapsed,
	panel: Panel,
	panelProps,
	width = "375px",
	onChange,
	enabled: enabledProp,
}: React.PropsWithChildren<CollapsiblePanelProps<PanelProps>>) {
	const viewportContext = useContext(ViewportContext);
	const enabled = enabledProp ?? viewportContext.isMobile;

	const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null);

	const toggleCollapsed = useCallback(() => {
		if (!enabled) return;
		onChange(!collapsed);
	}, [collapsed, enabled, onChange]);

	const contextValue = useMemo(
		() => ({
			collapsed,
			toggle: toggleCollapsed,
			set: onChange,
		}),
		[collapsed, toggleCollapsed, onChange],
	);

	if (!enabled)
		return (
			<div
				ref={setContentRef}
				className={cx(styles.main, "CollapsiblePanel--collapsed", "scroll-container")}
				data-test-id="collapsiblepanel__main"
			>
				<ScrollContext.Provider value={contentRef}>{children}</ScrollContext.Provider>
			</div>
		);

	return (
		<PanelCollapseContext.Provider value={contextValue}>
			<div className={cx(styles.outer, { [styles.collapsed]: collapsed })} style={{ "--collapsiblePanelWidth": width }}>
				<div className={styles.panelContainer}>
					<Panel {...panelProps} />
				</div>
				<div
					ref={setContentRef}
					className={cx(
						styles.main,
						collapsed ? "CollapsiblePanel--collapsed" : "CollapsiblePanel--open",
						"scroll-container",
					)}
					data-test-id="collapsiblepanel__main"
				>
					<ScrollContext.Provider value={contentRef}>{children}</ScrollContext.Provider>
				</div>
			</div>
		</PanelCollapseContext.Provider>
	);
}

/**
 * Default back button for use in a [[CollapsiblePanel]] layout on mobile.
 */
export const MobileOpenPanelButton = React.memo(function MobileOpenPanelButton(props: {
	className?: string;
	onClick?: React.EventHandler<React.MouseEvent>;
	icon?: IconDefinition;
}) {
	const context = useContext(PanelCollapseContext);

	const { onClick: propsOnClick } = props;
	const onClick = useMemo(
		() => (e: React.MouseEvent) => {
			context.set(false);
			propsOnClick?.(e);
			// We are listening `selectionchange` to show / hide the note's formatting toolbar,
			// but buttons don't trigger those changes, so we need a manual trigger on mobile
			// where left and right panel content are in the same URL.
			window.getSelection()?.removeAllRanges();
		},
		[propsOnClick, context],
	);
	if (props.icon) {
		return <FontAwesomeIcon className={styles.mobileBackIcon} icon={props.icon} onClick={onClick} />;
	}
	return (
		<button onClick={onClick} className={cx(styles.mobileOpenPanelButton, props.className)}>
			<PanelButtonIcon />
		</button>
	);
});
